import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { TDeliverableForm } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { TTextDescriptionDeliverableType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/text-description-deliverable/text-description-deliverable.interface';
import { EAnalyticsSentiment } from '@enums/campaign-analytics.enum';
import { EPlatformFeature } from '@enums/campaign.enum';
import {
  IKeywordLists,
  ITextFinding,
  TTextDescriptionDeliverableAnalyticsSync,
} from '@interfaces/analytics-details-utlilities.interface';
import { IFinding, IKeywordAnalytics } from '@interfaces/deliverable-analytics.interface';
import { DurationUtilitiesService } from './duration-utilities.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsDetailsUtilitiesService {
  /** Mirrors backend whitespace normalization: replace line breaks/tabs with spaces and collapse runs. */
  public static normalizeWhitespace(text: string): string {
    return text.replace(/[\r\n\t]+/g, ' ').replace(/ {2,}/g, ' ');
  }

  public static computeKeywordCount(
    keyword: string,
    keywords: IKeywordAnalytics<IFinding>[],
    removedIds: Set<string>,
  ): number | null {
    const kwData = keywords.find((k) => k.keyword === keyword);

    if (!kwData) return null;

    const findings = (kwData.findings ?? []).filter((f) => !removedIds.has(f.id));
    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(findings) as string[];

    return AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts([keyword], uniqueFindings)[keyword] ?? 0;
  }

  public static highlightKeywords(keywords: string[], text: string): string {
    const escapeHtml = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const escaped = escapeHtml(text);

    if (!keywords?.length) {
      return escaped;
    }

    const escapedKeywords = keywords.map(escapeHtml);
    const extendedKeywords = AnalyticsDetailsUtilitiesService.createExtendedKeywordList(escapedKeywords);
    const regex = AnalyticsDetailsUtilitiesService.getRegex(extendedKeywords);

    return escaped.replace(
      regex,
      '<span class="rounded-md font-bold text-xs bg-shikenso-success-light text-shikenso-success-base px-2 py-0.5">$&</span>',
    );
  }

  public static getIndividualKeywordCounts(keywords: string[], texts: string[]): Record<string, number> {
    const counts: Record<string, number> = {};

    keywords.forEach((keyword) => {
      counts[keyword] = 0;
    });

    // Check each keyword individually against all texts to allow overlapping matches
    keywords.forEach((keyword) => {
      const extendedKeywords = AnalyticsDetailsUtilitiesService.createExtendedKeywordList([keyword]);
      const regex = AnalyticsDetailsUtilitiesService.getRegex(extendedKeywords);

      texts.forEach((text) => {
        const matches = text.match(regex);

        if (matches) {
          counts[keyword] += matches.length;
        }
      });
    });

    return counts;
  }

  public static buildKeywordLists<T>(
    items: T[],
    keywordProp: keyof T,
    entriesProp: keyof T,
    icon = 'keyword.svg',
  ): IKeywordLists {
    const keywords = items.map((item) => String(item[keywordProp]));
    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(
      items.flatMap((item) => item[entriesProp] as ITextFinding),
    );

    const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywords, uniqueFindings as string[]);

    const keywordList = keywords.map((keyword) => ({
      count: counts[keyword] ?? 0,
      keyword,
    }));

    const kpiCardList = keywordList.map((item, idx) => ({
      icon,
      id: `${icon.replace('.svg', '')}-kpi-card-${idx}`,
      keyword: item.keyword,
      value: item.count,
    }));

    return { keywordList, kpiCardList };
  }

  public static buildKeywordRowIdMap<T extends ITextFinding & { id: string }>(
    entries: Array<{ keyword: string; finding: T }>,
  ): Record<string, string[]> {
    const keywordRowIds: Record<string, string[]> = {};
    const keywordSeenRowIds = new Map<string, Set<string>>();
    const findingRowIds = new Map<string, string>();

    entries.forEach(({ keyword, finding }) => {
      const findingKey = AnalyticsDetailsUtilitiesService.getTextFindingKey(finding);
      const rowId = findingRowIds.get(findingKey) ?? finding.id;

      findingRowIds.set(findingKey, rowId);

      if (!keywordRowIds[keyword]) {
        keywordRowIds[keyword] = [];
      }

      if (!keywordSeenRowIds.has(keyword)) {
        keywordSeenRowIds.set(keyword, new Set<string>());
      }

      const seenRowIds = keywordSeenRowIds.get(keyword);

      if (!seenRowIds?.has(rowId)) {
        seenRowIds?.add(rowId);
        keywordRowIds[keyword].push(rowId);
      }
    });

    return keywordRowIds;
  }

  public static buildTimestampedVideoUrl(
    platformFeature: EPlatformFeature | null | undefined,
    videoTimeSeconds: number | null | undefined,
    videoUrl: string | null | undefined,
  ): string | null {
    if (!videoUrl || videoTimeSeconds === null || videoTimeSeconds === undefined || videoTimeSeconds < 0) {
      return null;
    }

    try {
      const url = new URL(videoUrl);
      const formattedVideoTimeSeconds = Math.floor(videoTimeSeconds);

      if (platformFeature === EPlatformFeature.twitchStream) {
        url.searchParams.set('t', AnalyticsDetailsUtilitiesService.getTwitchVideoTime(formattedVideoTimeSeconds));

        return url.toString();
      }

      if (platformFeature === EPlatformFeature.youtubeStream || platformFeature === EPlatformFeature.youtubeVod) {
        url.searchParams.set('t', `${formattedVideoTimeSeconds}s`);

        return url.toString();
      }

      return null;
    } catch {
      return null;
    }
  }

  public static getSentimentIcon(sentiment: EAnalyticsSentiment): string {
    switch (sentiment) {
      case EAnalyticsSentiment.positive:
        return '/icons/mood-smile.svg';
      case EAnalyticsSentiment.negative:
        return '/icons/mood-sad.svg';
      case EAnalyticsSentiment.neutral:
        return '/icons/mood-empty.svg';
    }
  }

  public static getSentimentIconColor(sentiment: EAnalyticsSentiment): string {
    switch (sentiment) {
      case EAnalyticsSentiment.positive:
        return 'var(--shikenso-success-base)';
      case EAnalyticsSentiment.negative:
        return 'var(--shikenso-error-base)';
      case EAnalyticsSentiment.neutral:
        return 'var(--shikenso-gray-600)';
    }
  }

  public static formatVideoTime(videoTimeSeconds: number | null | undefined): string {
    if (
      videoTimeSeconds === null ||
      videoTimeSeconds === undefined ||
      isNaN(videoTimeSeconds) ||
      videoTimeSeconds < 0
    ) {
      return '-';
    }

    return DurationUtilitiesService.convertSecondsToVideoTime(videoTimeSeconds);
  }

  public static getUniqueTextFindings(findings: ITextFinding[], isTableData = false): string[] | ITextFinding[] {
    const uniqueFindings = new Map<string, ITextFinding>();

    findings.forEach((finding) => {
      const key = AnalyticsDetailsUtilitiesService.getTextFindingKey(finding);

      if (!uniqueFindings.has(key)) {
        uniqueFindings.set(key, finding);
      }
    });

    const sortedFindings = Array.from(uniqueFindings.values()).sort((a, b) => {
      const aTimestamp = new Date(a.timestamp).getTime();
      const bTimestamp = new Date(b.timestamp).getTime();

      return aTimestamp - bTimestamp;
    });

    if (isTableData) {
      return sortedFindings as ITextFinding[];
    }

    return sortedFindings.map((finding) => finding.text ?? finding.audio ?? '') as string[];
  }

  public static mapKeywordCountsToDeliverableForm(
    deliverableForm: FormGroup<TDeliverableForm>,
    keywordCounts: Record<string, number>,
  ): void {
    const form = deliverableForm;

    if (form && 'contents' in form.controls) {
      const contentsFormArray = form.controls.contents as FormArray;

      contentsFormArray.controls.forEach((contentControl) => {
        const textValue = contentControl.get('text')?.getRawValue();

        if (typeof textValue === 'string' && textValue in keywordCounts) {
          const count = keywordCounts[textValue];
          contentControl.get('analyticsCurrentCount')?.setValue(count, { emitEvent: false });
        }
      });
    }
  }

  public static mapTextDescriptionDeliverableAnalyticsToForm(
    deliverableForm: FormGroup<TDeliverableForm>,
    analytics: TTextDescriptionDeliverableAnalyticsSync | null | undefined,
    deliverableType: TTextDescriptionDeliverableType,
  ): void {
    const form = deliverableForm;

    if (!form?.controls || 'contents' in form.controls) {
      return;
    }

    if (form.controls.type?.value !== deliverableType) {
      return;
    }

    if (analytics === null || analytics === undefined) {
      form.get('analyticsCurrentCount')?.setValue(null, { emitEvent: false });
      form.get('analyticsCurrentRatio')?.setValue(null, { emitEvent: false });
      form.get('status')?.setValue(null, { emitEvent: false });

      return;
    }

    const count = analytics.findingsCount ?? analytics.findings?.length ?? null;
    const ratio = analytics.taskCoverage ?? null;
    const resolvedStatus = analytics.statusManual ?? analytics.status ?? null;

    form.get('analyticsCurrentCount')?.setValue(count, { emitEvent: false });
    form.get('analyticsCurrentRatio')?.setValue(ratio, { emitEvent: false });
    form.get('status')?.setValue(resolvedStatus, { emitEvent: false });
  }

  public static getTextFindingKey(finding: ITextFinding): string {
    return `${finding.audio}-${finding.text}-${finding.timestamp}-${finding.user}`;
  }

  public static isExternalVideoLinkPlatformFeature(platformFeature: EPlatformFeature | null | undefined): boolean {
    return (
      platformFeature === EPlatformFeature.twitchStream ||
      platformFeature === EPlatformFeature.youtubeStream ||
      platformFeature === EPlatformFeature.youtubeVod
    );
  }

  private static getRegex(keywords: string[]): RegExp {
    const escapedKeywords = AnalyticsDetailsUtilitiesService.escapeKeywords(keywords);
    const keywordsOrdered = [...escapedKeywords].sort((a, b) => b.length - a.length);

    return new RegExp(`(?<!\\w)(${keywordsOrdered.join('|')})(?!\\w)`, 'gi');
  }

  private static escapeKeywords(keywords: string[]): string[] {
    return keywords.map((kw) => AnalyticsDetailsUtilitiesService.escapeKeyword(kw));
  }

  private static escapeKeyword(keyword: string): string {
    return keyword.trim().replace(/[-\/\^$*+?.()|[\]{}]/g, '\\$&');
  }

  private static createExtendedKeywordList(keywords: string[]): string[] {
    const extendedKeywords: string[] = [];

    keywords.forEach((keyword) => {
      extendedKeywords.push(keyword);

      const noSpaceVersion = keyword.replace(/\s+/g, '');

      if (noSpaceVersion !== keyword && noSpaceVersion.length > 0) {
        extendedKeywords.push(noSpaceVersion);
      }
    });

    return extendedKeywords;
  }

  private static getTwitchVideoTime(videoTimeSeconds: number): string {
    const hours = Math.floor(videoTimeSeconds / 3600);
    const minutes = Math.floor((videoTimeSeconds % 3600) / 60);
    const seconds = Math.floor(videoTimeSeconds % 60);

    return `${hours}h${minutes}m${seconds}s`;
  }
}
